import { useAuth } from '@clerk/clerk-react';
import { type AnyRouter, RouterProvider } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { BlymLoading } from './components/loading';
import { type Brand } from './entity/api/brand';
import { type Organization } from './entity/api/organization';
// import { getConfig } from './config';
// import { SubscriptionStatus } from './entity/api/organization-billing';
import { type User } from './entity/api/user';
import { InternalServerError, NotFoundError } from './error';
import { trackError } from './error/tracking';
import { captureException } from './logger/capture-exception';
// import { requestBillingPortalUrl } from './sdk/billing';
import { getQueryClient } from './sdk/request';
import { groupPostHogWorkspace, identifyPostHogUser } from './services/posthog';
import { getStores } from './services/storage';
import { fetchBrandsListImperative, getBrandFromCache } from './services/storage/brands-queries';
import {
  fetchOrganizationBillingImperative,
  syncBillingImperative,
} from './services/storage/organization-billing-queries';
import { hasPendingOrganizationInvite } from './services/storage/organization-users-queries';
import { fetchOrganizationsListImperative } from './services/storage/organizations-queries';
import { UIKey } from './services/storage/ui-key-value';
import {
  fetchAuthUserImperative,
  getAuthUserFromCache,
  updateUserImperative,
  usersKeys,
} from './services/storage/users-queries';
import { changeLanguage } from './services/translation';
import { useToast } from './shared/contexts/toast-context';

const NO_PROVIDER_AUTH_TOKEN_ERR = 'no provider auth token';

function signOutAndRedirect() {
  return getStores()
    .users.getState()
    .signOut()
    .then(function () {
      // NOTE: we want window location so it completely refreshes
      window.location.href = '/auth';
      return null;
    });
}

// GET /user 404: Clerk session + token OK but no app user row yet — provision on /auth/wait-user
async function fetchAuthUserOrRedirectToWaitUser(): Promise<User | undefined> {
  const token = await Promise.resolve(getStores().users.getState().getAuthToken());
  if (token == null || token.length === 0) {
    throw new Error(NO_PROVIDER_AUTH_TOKEN_ERR);
  }

  try {
    const queryClient = getQueryClient();
    return await fetchAuthUserImperative(queryClient);
  } catch (err) {
    if (err instanceof NotFoundError) {
      // we force the recheck in case we are out of sync
      const tokenAgain = await Promise.resolve(getStores().users.getState().getAuthToken());
      if (tokenAgain == null || tokenAgain.length === 0) {
        throw err;
      }

      // there is a clerk user but not an app user, lets handle that
      window.location.href = '/auth/wait-user';
      return;
    }

    throw err;
  }
}

export function AppRouterAuth(props: { router: AnyRouter }) {
  const { isLoaded, userId } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const addToast = useToast();

  // Get queryClient early since we need it for cache checks
  const queryClient = getQueryClient();

  // auth cant go through without data required like for example auth, org, brand...
  useEffect(
    function () {
      if (!isLoaded) {
        return;
      }

      if (userId == null || userId.length === 0) {
        // NOTE: we want window location so it completely refreshes
        window.location.href = '/auth';
        return;
      }

      // Check if session has already been initialized for this tab
      // AND TanStack Query cache has the auth user
      const isSessionInitialized = getStores()
        .uiKeyValue.getState()
        .getFromStore(UIKey.SESSION_INITIALIZED);
      const cachedAuthUser = queryClient.getQueryData(usersKeys.authUser());

      if (isSessionInitialized === true && cachedAuthUser != null) {
        // Session already initialized and cache is populated, just show the app
        const loadingScreen = document.querySelector('#loading-screen');
        if (loadingScreen != null) {
          loadingScreen.remove();
        }
        setIsReady(true);
        return;
      }

      // If session was marked as initialized but cache is empty, clear the flag
      // This can happen if the page was refreshed
      if (isSessionInitialized === true && cachedAuthUser == null) {
        getStores().uiKeyValue.getState().setToStore(UIKey.SESSION_INITIALIZED, false);
      }

      // make sure we have a token before we proceed

      // TO-DO: (HIGH PRIO) Get rid of this when refactoring signup/signin
      async function fetchOrganizationsWithRetry(
        authUser: User,
        retryCount = 0,
        maxRetries = 3
      ): Promise<[Organization[], Brand[]]> {
        const [organizations, brands] = await Promise.all([
          fetchOrganizationsListImperative(queryClient),
          fetchBrandsListImperative(queryClient),
        ]);

        const orgIsPresent =
          authUser.selectedOrganizationId != null && authUser.selectedOrganizationId.length > 0;

        // If no organizations and no selectedOrganizationId, retry after delay
        if (organizations.length === 0 && !orgIsPresent) {
          if (retryCount < maxRetries) {
            // Wait 1 second before retrying
            await new Promise(function (resolve) {
              setTimeout(function () {
                resolve(undefined);
              }, 1000);
            });
            // Refresh auth user in case it was updated
            const refreshedUser = await fetchAuthUserOrRedirectToWaitUser();
            if (refreshedUser == null) {
              await signOutAndRedirect();
              throw new Error('User not found after retry - signed out');
            }

            return fetchOrganizationsWithRetry(refreshedUser, retryCount + 1, maxRetries);
          }
          // All retries exhausted, sign out
          await signOutAndRedirect();

          throw new Error('No organizations found after retries - signed out');
        }

        // Clear UTM values from sessionStorage after successful signup
        // This happens when organizations are fetched, indicating the user has completed signup
        if (organizations.length > 0) {
          getStores().uiKeyValue.getState().clearUtmValues?.();
        }

        return [organizations, brands];
      }

      Promise.resolve(getStores().users.getState().getAuthToken())
        .then(function (token) {
          if (token == null || token.length === 0) {
            // NOTE: we want window location so it completely refreshes
            window.location.href = '/auth';

            // Throw to stop the promise chain
            throw new Error(NO_PROVIDER_AUTH_TOKEN_ERR);
          }

          // Use TanStack Query to fetch and cache auth user
          return fetchAuthUserOrRedirectToWaitUser();
        })
        .then(async function (authUser) {
          if (authUser == null) {
            return signOutAndRedirect();
          }

          // TODO: (HIGH PRIO) Get rid of this when refactoring signup/signin - Fetch organizations and brands with retry logic
          const [organizations, brands] = await fetchOrganizationsWithRetry(authUser);

          // Refresh auth user in case it was updated during retries
          const refreshedUser = await fetchAuthUserOrRedirectToWaitUser();
          if (refreshedUser == null) {
            return signOutAndRedirect();
          }

          return [refreshedUser, organizations, brands];
        })
        .then(async function (data) {
          const authUser = data?.[0] as User;
          const organizations = (data?.[1] as Organization[]) ?? ([] as Organization[]);
          const brands = (data?.[2] as Brand[]) ?? ([] as Brand[]);

          if (authUser == null) {
            return signOutAndRedirect();
          }

          const orgIsPresent =
            authUser.selectedOrganizationId != null && authUser.selectedOrganizationId.length > 0;

          // Ensure we have at least one organization before accessing organizations[0]
          if (organizations.length === 0 && !orgIsPresent) {
            return signOutAndRedirect();
          }

          // If selectedOrganizationId is empty but selectedBrandId is valid, derive org from brand
          const { selectedBrandId } = authUser;
          if (!orgIsPresent && selectedBrandId != null && selectedBrandId.length > 0) {
            // Get brand from TanStack Query cache
            const brandFromSelection = getBrandFromCache(queryClient, selectedBrandId);
            if (brandFromSelection != null) {
              // Verify the brand's org is accessible
              const brandOrg = organizations.find(
                (org) => org._id === brandFromSelection.organizationId
              );
              if (brandOrg != null) {
                authUser.selectedOrganizationId = brandOrg._id;
                // selectedBrandId is already correct, no need to change it
              }
            }
          }

          // Ensure that we are still part of the organisation
          const isStillPartOfOrg = organizations?.find(
            (org) => org._id === authUser.selectedOrganizationId
          );

          // If not, target first available Org/Brand and persist the change
          if (isStillPartOfOrg == null) {
            // Get brand from the fetched list (now direct array, not { data: Brand[] })
            const newBrandSelection = brands[0];
            const newOrgId = newBrandSelection?.organizationId ?? organizations[0]?._id ?? '';
            const newBrandId = newBrandSelection?._id ?? '';

            // Check cache before mutating so "already same" uses pre-mutation values (avoids skipping PATCH)
            const cachedUser = getAuthUserFromCache(queryClient);
            const alreadySame =
              cachedUser?.selectedOrganizationId === newOrgId &&
              cachedUser?.selectedBrandId === newBrandId;

            if (newOrgId.length > 0 && newBrandId.length > 0 && !alreadySame) {
              await updateUserImperative(queryClient, {
                selectedOrganizationId: newOrgId,
                selectedBrandId: newBrandId,
              });
            }

            // Update local authUser object for rest of function (cache is updated by updateUserImperative on success)
            authUser.selectedOrganizationId = newOrgId;
            authUser.selectedBrandId = newBrandId;
          }

          const selOrgId = authUser.selectedOrganizationId ?? '';
          if (selOrgId.length === 0) {
            captureException(
              new InternalServerError('user has no organizations'),
              {
                file: 'router-auth.tsx',
                method: 'useEffect',
                infoMessage: 'user has no organizations',
                organizationId: selOrgId,
                authUserId: authUser._id,
              },
              trackError
            );

            // identify with user data only — no org available at this point
            identifyPostHogUser(authUser);
            return [authUser, organizations, brands];
          }

          // we want to make sure that at this stage, the organization billing is in sync
          // with the payment provider (using TanStack Query)
          await syncBillingImperative(queryClient, selOrgId).catch(function (err) {
            captureException(
              err,
              {
                file: 'router-auth.tsx',
                method: 'syncBillingImperative',
                infoMessage: 'unable to manage sync billing with payment provider',
                organizationId: selOrgId,
              },
              trackError
            );
          });

          // Fetch and cache organization billing using TanStack Query
          const orgBilling = await fetchOrganizationBillingImperative(queryClient, selOrgId);
          const selectedOrg = organizations.find((org) => org._id === selOrgId);
          groupPostHogWorkspace(selectedOrg, orgBilling);
          identifyPostHogUser(authUser, orgBilling ?? undefined);

          return [authUser, organizations, brands, orgBilling];
        })
        .then(function (data) {
          const authUser = data?.[0] as User;
          const organizations = (data?.[1] as Organization[]) ?? ([] as Organization[]);

          if (authUser == null) {
            return signOutAndRedirect();
          }

          // first check if user has pending invites - this takes priority
          // Organization users are already cached by fetchOrganizationsListImperative
          const hasOrgInvite = hasPendingOrganizationInvite(
            queryClient,
            authUser._id,
            authUser.email
          );
          if (hasOrgInvite && window.location.href.indexOf('/invite') === -1) {
            window.location.href = '/app/invite';
            return;
          }

          if (authUser.language != null) {
            void changeLanguage(authUser.language);
          }

          // only after handling invites, check if user needs an org or brand and also check that the route is not /invite
          if (
            (organizations == null || organizations.length === 0) &&
            window.location.href.indexOf('/invite') === -1
          ) {
            return signOutAndRedirect();
          }

          const selectedOrganization = organizations.find(
            (org) => org._id === authUser.selectedOrganizationId
          );

          if (
            selectedOrganization != null &&
            selectedOrganization.hasCompletedOnboarding === false &&
            window.location.href.indexOf('/onboarding') === -1
          ) {
            window.location.href = '/app/onboarding';
            return;
          }

          // if (getConfig().features.subscription.enabled) {
          //   // organization might have a pending payment
          //   // we need to send the user to pay in these cases
          //   const org = organizations.find(
          //     (o) => o.billingPlan.currentPlanStatus === SubscriptionStatus.Trialing
          //   );
          //   if (org != null) {
          //     return requestBillingPortalUrl(org._id).then(function (data) {
          //       window.location.href = data.url;
          //     });
          //   }
          // }

          // Mark session as initialized for this tab
          getStores().uiKeyValue.getState().setToStore(UIKey.SESSION_INITIALIZED, true);

          // remove the loading screen, not required at this point by unauth
          const loadingScreen = document.querySelector('#loading-screen');
          if (loadingScreen != null) {
            loadingScreen.remove();
          }

          setIsReady(true);
        })
        .catch(function (err) {
          // Don't process if we're already redirecting (token missing case)
          if (err instanceof Error && err.message === NO_PROVIDER_AUTH_TOKEN_ERR) {
            return;
          }

          // Don't process if sign-out was already handled in retry function
          // These errors are thrown after signOutAndRedirect() is called, so we should
          // not track them as errors or call signOutAndRedirect() again
          if (
            err instanceof Error &&
            (err.message === 'User not found after retry - signed out' ||
              err.message === 'No organizations found after retries - signed out')
          ) {
            return; // Sign-out already handled, don't track error or sign out again
          }

          captureException(
            err,
            {
              file: 'router-auth.tsx',
              method: 'allSettled',
              infoMessage: 'unable to manage requirements',
            },
            trackError,
            addToast
          );

          // force the sign out on this case
          return signOutAndRedirect();
        });
    },
    [isLoaded, userId, addToast]
  );

  // wait for the all data to load on the app and then go through
  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex h-screen w-screen flex-col items-center justify-center bg-white ${isReady ? 'opacity-0' : 'opacity-100'} pointer-events-none transition-opacity duration-500 ease-out`}
        id="loading-screen-blym"
      >
        <BlymLoading />
      </div>
      {isReady ? <RouterProvider router={props.router} notFoundMode="root" /> : null}
    </>
  );
}
